import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'
import { VoiceData, VoiceParams } from 'hooks/useBrandVoices'
import { CTA } from 'stores/OnboardingStore'
import { useRootStore } from 'stores/RootStore'

export interface Data extends VoiceData {}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export interface Variables {
  voiceParams: VoiceParams
  brandKitId?: number
}

export const createBrandVoice = async ({
  workspaceId,
  brandKitId,
  voiceParams,
}: Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_voices`, {
    method: 'POST',
    body: { voice_params: voiceParams, brand_kit_id: brandKitId },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to create brand voice', {
      metaData: {
        voiceParams,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useCreateBrandVoice = (options?: Options) => {
  const queryClient = useQueryClient()
  const { onboardingStore } = useRootStore()
  const currentWorkspaceId = useCurrentWorkspaceId()
  return useMutation<Data, unknown, Variables>(
    'createBrandVoice',
    async (variables) => {
      const { brandKitId } = variables
      const result = await createBrandVoice({ workspaceId: currentWorkspaceId, ...variables })
      onboardingStore.markCTAAsCompleted(CTA.BrandVoice)
      queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandVoices', 'list'])
      if (brandKitId) {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', brandKitId])
      }
      return result
    },
    {
      ...options,
    },
  )
}
